import { eCommerceClient } from 'axios/client';
import { IconPackage, IconStarFill } from 'components/icons';
import { E_COMMERCE_STORAGE_URL, SITE_MAP } from 'globalConstants';
import { ceil, isEmpty, isEqual, lt, map, size, take } from 'lodash';
import moment from 'moment-timezone';
import Link from 'next/link';
import {
  DEFAULT_LOAD_MORE_LIMIT_COMPANY_CARD_ITEM,
  DEFAULT_SKELETON_COMPANY_CARD_ITEM,
} from 'pages/thuong-mai-dien-tu/nha-cung-cap';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  CompaniesParams,
  CompaniesResponse,
  CompaniesSerialization,
  CompanySerialization,
  RequiredType,
} from 'restful/e-commerce';

const MainContent = () => {
  const [companies, setCompanies] = useState<CompanySerialization[] | undefined>();
  const [stage, setStage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data: companiesData } = await eCommerceClient.get<CompaniesParams, CompaniesResponse>(
        `/v1/companies`,
        {
          params: {
            top_ten: RequiredType.N,
          },
        },
      );
      if (isEqual(companiesData?.status, 'success')) {
        setLoading(false);
        setCompanies((companiesData?.data as CompaniesSerialization).data);
      }
    })();
  }, []);

  return (
    <InfiniteScroll
      dataLength={size(take(companies, stage * DEFAULT_LOAD_MORE_LIMIT_COMPANY_CARD_ITEM))}
      hasMore={lt(
        size(take(companies, stage * DEFAULT_LOAD_MORE_LIMIT_COMPANY_CARD_ITEM)),
        size(companies),
      )}
      loader={null}
      next={() => setStage(stage + 1)}
    >
      <div className='flex flex-col space-y-[24px] w-full'>
        {!loading && isEmpty(companies) ? (
          <div className='flex w-full flex-col items-center space-y-[28px]'>
            <div className='h-[200px] w-full bg-[url("/images/no-data-found.svg")] bg-contain bg-center bg-no-repeat' />
            <span className='text-text2'>Chưa có nhà cung cấp nào</span>
          </div>
        ) : (
          <div className='flex flex-col space-y-[24px]'>
            {loading
              ? map(Array(DEFAULT_SKELETON_COMPANY_CARD_ITEM).fill({}), (_, skeletonIndex) => (
                  <div key={skeletonIndex} className='bg-paper rounded-[8px]'>
                    <div className='p-[16px] animate-pulse flex space-x-[24px]'>
                      <div className='rounded-[6px] bg-secondary min-w-[80px] h-[80px]' />
                      <div className='flex w-full justify-between space-x-[24px]'>
                        <div className='flex flex-col space-y-[12px]'>
                          <div className='w-[120px] h-[30px] flex items-center'>
                            <div className='w-full bg-secondary h-[24px]'> </div>
                          </div>
                          <div className='w-[360px] h-[18px] flex items-center'>
                            <div className='w-full bg-secondary h-[14px]'> </div>
                          </div>
                          <div className='w-[300px] h-[18px] flex items-center'>
                            <div className='w-full bg-secondary h-[14px]'> </div>
                          </div>
                        </div>
                        <div className='flex items-center self-center space-x-[24px]'>
                          <div className='flex flex-col items-center space-y-[10px]'>
                            <div className='w-[40px] h-[20px] flex items-center'>
                              <div className='w-full bg-secondary h-[16px]'> </div>
                            </div>
                            <div className='w-[60px] h-[18px] flex items-center'>
                              <div className='w-full bg-secondary h-[14px]'> </div>
                            </div>
                          </div>
                          <div className='w-[1px] h-[52px] bg-stroke' />
                          <div className='flex flex-col items-center space-y-[10px]'>
                            <div className='w-[40px] h-[20px] flex items-center'>
                              <div className='w-full bg-secondary h-[16px]'> </div>
                            </div>
                            <div className='w-[60px] h-[18px] flex items-center'>
                              <div className='w-full bg-secondary h-[14px]'> </div>
                            </div>
                          </div>
                          <div className='w-[1px] h-[52px] bg-stroke' />
                          <div className='flex h-[38px] w-[90px] rounded-[6px] bg-secondary' />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              : map(
                  take(companies, stage * DEFAULT_LOAD_MORE_LIMIT_COMPANY_CARD_ITEM),
                  (company, companyIndex) => {
                    const {
                      company_id,
                      logo_url,
                      company: companyName,
                      address,
                      average_rating,
                      products_count,
                      timestamp,
                    } = company;
                    return (
                      <div
                        key={`${company_id}-${companyIndex}`}
                        className='bg-paper p-[16px] rounded-[8px]'
                      >
                        <div className='flex space-x-[24px]'>
                          <div className='min-w-[80px] max-w-[80px] h-[80px] flex items-center justify-center rounded-[6px] border border-stroke overflow-hidden'>
                            <img
                              src={
                                logo_url
                                  ? `${E_COMMERCE_STORAGE_URL}/images/${logo_url}`
                                  : '/images/error-image.svg'
                              }
                              alt={
                                logo_url
                                  ? `${E_COMMERCE_STORAGE_URL}/images/${logo_url}`
                                  : '/images/error-image.svg'
                              }
                              loading='lazy'
                              className='w-full h-full'
                            />
                          </div>
                          <div className='flex w-full justify-between space-x-[24px]'>
                            <div className='flex flex-col space-y-[12px]'>
                              {companyName && (
                                <span className='font-[600] text-[24px] leading-[30px] line-clamp-1'>
                                  {companyName}
                                </span>
                              )}
                              {address && <span className='line-clamp-1'>{address}</span>}
                              {timestamp && (
                                <span className='line-clamp-1'>
                                  Ngày tham gia: {moment(timestamp).format('DD-MM-YYYY')}
                                </span>
                              )}
                            </div>
                            <div className='flex items-center self-center space-x-[24px]'>
                              <div className='flex flex-col items-center space-y-[10px] whitespace-nowrap'>
                                <div className='flex items-center space-x-[12px]'>
                                  <IconPackage className='w-[24px] h-[24px]' />
                                  <span className='text-[16px] leading-[20px] font-[600]'>
                                    {products_count ? products_count.toLocaleString() : 0}
                                  </span>
                                </div>
                                <span className='text-text2 text-center'>Sản phẩm</span>
                              </div>
                              <div className='w-[1px] h-[52px] bg-stroke' />
                              <div className='flex flex-col items-center space-y-[10px] whitespace-nowrap'>
                                <div className='flex items-center space-x-[12px]'>
                                  <IconStarFill className='w-[24px] h-[24px] text-e-commerce-rating' />
                                  <span className='text-[16px] leading-[20px] font-[600]'>
                                    {average_rating ? ceil(average_rating).toLocaleString() : 0}
                                  </span>
                                </div>
                                <span className='text-text2 text-center'>Đánh giá</span>
                              </div>
                              <div className='w-[1px] h-[52px] bg-stroke' />
                              <Link
                                href={
                                  company_id
                                    ? `/${SITE_MAP.E_COMMERCE.INDEX}/${SITE_MAP.E_COMMERCE.COMPANY.INDEX}/${company_id}`
                                    : '#'
                                }
                                className='flex h-[38px] items-center justify-center rounded-[6px] px-[12px] bg-secondary border border-solid border-primary transition duration-[200ms] ease-in-out hover:bg-secondary-light whitespace-nowrap'
                              >
                                <span className='font-[600] text-primary text-center'>
                                  Xem shop
                                </span>
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  },
                )}
          </div>
        )}
      </div>
    </InfiniteScroll>
  );
};

export default MainContent;
